import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FlagOrderDialog } from "./FlagOrderDialog";

const SAMPLES = [
  {
    id: "new-york",
    name: "Hyrox New York",
    detail: "Men · Relay · 40+ · USA · Season 25/26",
    image: "/products/flag-new-york.jpg",
  },
  {
    id: "vancouver",
    name: "Hyrox Vancouver",
    detail: "Men · Doubles · 40–44 · CAN · Season 25/26",
    image: "/products/flag-vancouver.jpg",
  },
  {
    id: "the-pair",
    name: "The Pair",
    detail: "Vancouver & New York, hung together",
    image: "/products/flag-pair.jpg",
  },
];

const eyebrow =
  "font-mono text-[11px] tracking-[0.25em] uppercase text-mutedink";

export const HyroxFlags = () => {
  const [orderOpen, setOrderOpen] = useState(false);
  return (
    <div data-testid="hyrox-flags-page" className="min-h-screen flex flex-col">
      <header className="border-b border-ink/10 px-6 py-4 bg-[#F9F8F5]/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            data-testid="hyrox-flags-back-link"
            className="inline-flex items-center gap-2 text-sm text-mutedink hover:text-oxblood transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Portfolio
          </Link>
          <span className="font-cinzel text-xs sm:text-sm tracking-[0.25em] text-ink">
            HAILEY <span className="text-bronze">&amp;</span> CHLOE
          </span>
        </div>
      </header>

      <main className="flex-1 px-6 py-16 sm:py-20">
        <div className="max-w-5xl mx-auto">
          <p className={`${eyebrow} mb-4`}>
            A Hailey &amp; Chloe Holdings Project
          </p>
          <h1 className="font-serif font-light tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl">
            HYROX FLAGS
          </h1>
          <p
            data-testid="hyrox-flags-tagline"
            className="mt-3 font-serif italic text-xl text-oxblood"
          >
            Every race deserves a flag.
          </p>
          <p className="mt-6 max-w-xl text-base font-light text-[#2A2A2A] leading-relaxed">
            Custom finisher flags for Hyrox athletes — one city, one season, one
            division per flag. Printed on weatherproof polyester with brass
            grommets, sized to hang in a gym, a garage or above the desk you
            trained for it at.
          </p>

          <section data-testid="hyrox-flags-samples" className="mt-14">
            <p className={`${eyebrow} mb-3`}>Samples</p>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink mb-8">
              Flags we have made.
            </h2>
            <p
              data-testid="hyrox-flags-price"
              className="-mt-4 mb-8 inline-flex items-baseline gap-3 font-mono text-sm text-oxblood"
            >
              $50 USD
              <span className="text-[10px] uppercase tracking-[0.2em] text-mutedink">
                per flag · plus shipping, worldwide
              </span>
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {SAMPLES.map((s) => (
                <article
                  key={s.id}
                  data-testid={`flag-sample-${s.id}`}
                  className="bg-white border border-ink/10 hover:border-oxblood/40 transition-colors duration-300 flex flex-col"
                >
                  <div className="overflow-hidden aspect-[3/4]">
                    <img
                      src={s.image}
                      alt={s.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">
                      {s.detail}
                    </p>
                    <h3 className="mt-1.5 font-serif text-xl text-ink">
                      {s.name}
                    </h3>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section
            data-testid="hyrox-flags-disclaimer"
            className="mt-20 border-t border-ink/10 pt-14"
          >
            <p className={`${eyebrow} mb-3`}>Disclaimer</p>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink mb-6">
              Replica only.
            </h2>
            <div className="bg-white border border-oxblood/30 p-6 sm:p-8 max-w-3xl">
              <ul className="space-y-3 text-sm text-[#2A2A2A] leading-relaxed list-disc pl-5">
                <li data-testid="disclaimer-replica">
                  These flags are <strong>replicas</strong> — almost 1:1
                  reproductions of the official Hyrox finisher flag. They are
                  not official Hyrox merchandise and are not affiliated with or
                  endorsed by Hyrox.
                </li>
                <li data-testid="disclaimer-owners">
                  We make them{" "}
                  <strong>
                    only for athletes who already possess the original flag
                  </strong>{" "}
                  for that race. You will be asked to show your original before
                  we print.
                </li>
                <li data-testid="disclaimer-purpose">
                  Intended as an <strong>extra copy</strong> to showcase at the
                  gym or at home, so the original stays protected.
                </li>
                <li data-testid="disclaimer-price">
                  <strong>$50 USD</strong> per flag, plus shipping to anywhere
                  in the world — quoted to your address before payment.
                </li>
              </ul>
            </div>
          </section>

          <section
            data-testid="hyrox-flags-custom"
            className="mt-20 border-t border-ink/10 pt-14"
          >
            <p className={`${eyebrow} mb-3`}>Made to order</p>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink mb-4">
              Your city. Your season.
            </h2>
            <p className="max-w-xl text-sm text-mutedink leading-relaxed">
              Enter the race city, division, age group and season, attach a
              photo of your original flag, and we will reproduce it. We review
              the photo first, then send a payment link.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="hyrox-flags-order-btn"
                onClick={() => setOrderOpen(true)}
                className="inline-flex items-center gap-2 bg-ink text-parchment px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-oxblood transition-colors duration-300"
              >
                Order a replica — $50 USD + shipping
              </button>
              <Link
                to="/#inquiries"
                data-testid="hyrox-flags-enquire-link"
                className="inline-flex items-center gap-2 border border-ink/25 text-ink px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-oxblood hover:text-oxblood transition-colors duration-300"
              >
                Ask a question
              </Link>
            </div>
            <FlagOrderDialog open={orderOpen} onOpenChange={setOrderOpen} />
          </section>
        </div>
      </main>

      <footer className="bg-[#111111] text-[#F9F8F5] px-6 py-8">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="font-cinzel text-sm tracking-[0.2em]">
            HYROX <span className="text-bronze">FLAGS</span>
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F9F8F5]/40">
            © Hailey and Chloe Holdings Inc.
          </p>
        </div>
      </footer>
    </div>
  );
};
